import { useCallback, useEffect, useState } from "react";
import { getMechanicInvoices } from "../../../services/api/invoiceApi";
import { getMyMechanicProfile } from "../../../services/api/mechanicApi";
import { getCustomers } from "../../../services/api/customerApi";

const EMPTY_STATS = { today: 0, month: 0, year: 0, customers: 0 };

function mergeServiceLogs(profileLogs = [], invoices = []) {
  const uniqueLogs = new Map();

  [...profileLogs, ...invoices.map((invoice) => invoice?.serviceLog)]
    .filter((service) => service?.id && service?.serviceDate)
    .forEach((service) => uniqueLogs.set(String(service.id), service));

  return [...uniqueLogs.values()];
}

export function countInvoiceCustomers(invoices = []) {
  const customerIds = new Set(
    invoices
      .map((invoice) => invoice?.vehicle?.userId ?? invoice?.vehicle?.user?.id)
      .filter((customerId) => customerId !== null && customerId !== undefined)
      .map(String)
  );

  return customerIds.size;
}

export function countCustomers(customerResult, invoices = []) {
  const total = Number(customerResult?.total);
  return Number.isFinite(total) && total >= 0 ? total : countInvoiceCustomers(invoices);
}

export function calculateServiceStats(serviceLogs, customerCount, now = new Date()) {
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const currentDay = now.getDate();

  return serviceLogs.reduce(
    (result, service) => {
      const serviceDate = new Date(service.serviceDate);
      if (Number.isNaN(serviceDate.getTime())) return result;

      if (serviceDate.getFullYear() === currentYear) {
        result.year += 1;
        if (serviceDate.getMonth() === currentMonth) {
          result.month += 1;
          if (serviceDate.getDate() === currentDay) result.today += 1;
        }
      }
      return result;
    },
    { ...EMPTY_STATS, customers: Math.max(0, Number(customerCount) || 0) }
  );
}

function getErrorMessage(error) {
  return error?.response?.data?.message || error?.message || "دریافت آمار با مشکل روبه‌رو شد";
}

export default function useServiceDashboardStats() {
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;

    const loadStats = async () => {
      setLoading(true);
      setError("");

      const [profileResult] = await Promise.allSettled([
        getMyMechanicProfile(),
      ]);

      if (cancelled) return;

      const profile =
        profileResult.status === "fulfilled" ? profileResult.value?.data : null;

      let invoices = [];
      let invoiceError = null;
      if (profile?.id) {
        try {
          const response = await getMechanicInvoices(profile.id);
          invoices = Array.isArray(response?.data) ? response.data : [];
        } catch (requestError) {
          invoiceError = requestError;
        }
      }

      let customerResult = null;
      let customerError = null;
      try {
        customerResult = await getCustomers({ page: 1, vehicles: "all", sort: "newest" });
      } catch (requestError) {
        customerError = requestError;
      }

      if (cancelled) return;

      const logs = mergeServiceLogs(profile?.serviceLogs, invoices);
      const customerCount = countCustomers(customerResult, invoices);
      setStats(calculateServiceStats(logs, customerCount));

      const failedRequest =
        profileResult.status === "rejected"
          ? profileResult.reason
          : invoiceError || customerError;

      if (failedRequest) setError(getErrorMessage(failedRequest));
      setLoading(false);
    };

    loadStats();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return { stats, loading, error, reload };
}
